import { BAND_CLASS } from "./RamsHazardTable";
import { AeRouteMap } from "./NearestAeBox";

/** Read-only RAMS rendering for operatives (and admins previewing). `mapSrc` = API path for the A&E route map (portal or staff). */
export default function RamsReadView({ rams, permitTypes = [], mapSrc = `/in-house-portal/rams/${rams.id}/ae-map.png` }) {
  const job = rams.job || {};
  const em = rams.emergency || {};
  const ae = em.nearest_ae && em.nearest_ae.name ? em.nearest_ae : null;
  const permitLabel = (k) => (permitTypes.find(p => p.key === k) || {}).label || k.replace(/_/g, " ");
  return (
    <div className="space-y-4 text-xs" data-testid={`rams-read-${rams.id}`}>
      {(rams.summary || rams.scope_of_works) && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Scope of works</h4>
          {rams.summary && <p className="mb-1">{rams.summary}</p>}
          {rams.scope_of_works && <p className="whitespace-pre-wrap text-muted-foreground">{rams.scope_of_works}</p>}
        </section>
      )}
      {(job.property_address || job.site_contact || job.safety_notice) && (
        <section className="grid sm:grid-cols-2 gap-2">
          {job.property_address && <p><span className="text-muted-foreground">Site:</span> {job.property_address}</p>}
          {job.site_contact && <p><span className="text-muted-foreground">Site contact:</span> {job.site_contact}</p>}
          {job.safety_notice && <p className="sm:col-span-2 bg-red-50 text-red-800 border border-red-200 rounded p-2">{job.safety_notice}</p>}
        </section>
      )}
      {(rams.ppe || []).length > 0 && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">PPE required</h4>
          <div className="flex flex-wrap gap-1">{rams.ppe.map(p => <span key={p} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{p}</span>)}</div>
        </section>
      )}
      {(rams.permits || []).length > 0 && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Permits required</h4>
          <div className="flex flex-wrap gap-1">{rams.permits.map(p => <span key={p} className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">{permitLabel(p)}</span>)}</div>
        </section>
      )}
      {(rams.hazards || []).length > 0 && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Hazards &amp; controls</h4>
          <ul className="space-y-1.5">
            {rams.hazards.map((h, i) => (
              <li key={h.id || i} className="border border-border rounded p-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">{h.hazard}<span className="text-muted-foreground font-normal"> — {h.who}</span></p>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${BAND_CLASS[h.residual_band] || BAND_CLASS.low}`}>{h.residual} {(h.residual_band || "").replace("_", " ")}</span>
                </div>
                <p className="text-muted-foreground mt-0.5">{h.controls}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(rams.method_steps || []).length > 0 && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Method statement</h4>
          <ol className="space-y-1">
            {rams.method_steps.map((s, i) => <li key={s.id || i} className="flex gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">{i + 1}</span><div><p className="font-semibold">{s.step}</p><p className="text-muted-foreground">{s.detail}</p></div></li>)}
          </ol>
        </section>
      )}
      {(rams.coshh || []).length > 0 && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">COSHH</h4>
          <ul className="space-y-0.5">{rams.coshh.map((c, i) => <li key={c.id || i}><span className="font-semibold">{c.substance}</span> — {c.hazard}. <span className="text-muted-foreground">{c.controls}</span></li>)}</ul>
        </section>
      )}
      {(Object.values(em).some(Boolean)) && (
        <section>
          <h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Emergency arrangements</h4>
          <div className="grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
            {em.first_aider && <p><span className="text-muted-foreground">First aider:</span> {em.first_aider}</p>}
            {em.first_aid_location && <p><span className="text-muted-foreground">First-aid kit:</span> {em.first_aid_location}</p>}
            {em.hospital && !ae && <p><span className="text-muted-foreground">Nearest A&amp;E:</span> {em.hospital}</p>}
            {em.assembly_point && <p><span className="text-muted-foreground">Assembly point:</span> {em.assembly_point}</p>}
            {em.site_contact && <p><span className="text-muted-foreground">Emergency contact:</span> {em.site_contact}</p>}
          </div>
          {ae && (
            <div className="mt-2 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3 grid md:grid-cols-[1fr_minmax(180px,45%)] gap-3 items-start" data-testid="read-ae-box">
              <div className="space-y-0.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">Nearest A&amp;E</p>
                <p className="text-sm font-semibold" data-testid="read-ae-name">{ae.name}</p>
                {ae.address && <p className="text-muted-foreground">{ae.address}</p>}
                {ae.phone && <p><a href={`tel:${ae.phone}`} className="text-primary hover:underline" data-testid="read-ae-phone">{ae.phone}</a></p>}
                {ae.distance_text && <p className="text-muted-foreground">{ae.distance_text}</p>}
                {ae.directions_url && <a href={ae.directions_url} target="_blank" rel="noreferrer" className="inline-block mt-1 text-primary font-medium hover:underline" data-testid="read-ae-directions">Get directions in Google Maps →</a>}
              </div>
              {ae.lat != null && ae.site?.lat != null && mapSrc && <AeRouteMap src={mapSrc} directionsUrl={ae.directions_url} testId="read-ae-map" />}
            </div>
          )}
          {em.procedures && <p className="text-muted-foreground whitespace-pre-wrap mt-1">{em.procedures}</p>}
        </section>
      )}
      {rams.welfare && <section><h4 className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Welfare, environment &amp; waste</h4><p className="text-muted-foreground whitespace-pre-wrap">{rams.welfare}</p></section>}
    </div>
  );
}
